// src/views/Seguridad/Permisos/EditarPermiso.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaSave } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const ACCIONES = ["UPDATE", "LIST", "INSERT", "DELETE", "VIEW"];

/**
 * Props:
 * - initial: { id, menuNombre, moduloNombre, subModuloNombre, accion }
 * - onClose / onVolver: () => void
 * - onSaved / onGuardado: (updatedDto) => void
 */
export default function EditarPermiso({
  initial,
  onClose,
  onSaved,
  onVolver,
  onGuardado,
}) {
  // Compat de callbacks (usa el que venga)
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || onGuardado || (() => {});

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFn]);

  const id = useMemo(() => initial?.id ?? null, [initial]);
  const [accion, setAccion] = useState(initial?.accion || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // API URL (helper o BASE)
const loginStr = apiRoutes?.login ? String(apiRoutes.login) : "";
const API_BASE = loginStr.replace(/\/login.*$/, "") || "/flpa/api";

const UPDATE_URL = apiRoutes?.permisos?.updateAccion
  ? String(apiRoutes.permisos.updateAccion)
  : `${API_BASE}/permisos/accion`;

  // ¿Hubo cambios?
  const changed =
    (accion || "").trim().toUpperCase() !==
    (initial?.accion || "").trim().toUpperCase();

  const handleSave = async () => {
    if (!id || !changed) return;
    setError("");
    setSaving(true);
    try {
      const body = { id: Number(id), accion: String(accion).toUpperCase() };
      const updated = await apiFetchJson(UPDATE_URL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      // Notificar al padre y cerrar
      savedFn(updated);
      closeFn();
    } catch (e) {
      setError(e?.message || "Ocurrió un error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="section-title">Editar Permiso</h2>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            {/* ID oculto */}
            {/* <input type="hidden" value={id ?? ""} readOnly /> */}

            {/* Solo lectura */}
            <Fila label="Menú" value={initial?.menuNombre ?? "—"} />
            <Fila label="Módulo" value={initial?.moduloNombre ?? "—"} />
            <Fila label="Submódulo" value={initial?.subModuloNombre ?? "—"} />

            {/* Editable: Permiso */}
            <FilaEdit
              label="Permiso"
              input={
                <select
                  className="input"
                  value={(accion || "").toUpperCase()}
                  onChange={(e) => setAccion(e.target.value)}
                >
                  {ACCIONES.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              }
              help="Seleccioná el nuevo valor para PER_DS_PERMISO."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="btn-row">
            <button
              type="button"
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={!changed || saving}
              title={!changed ? "No hay cambios para guardar" : "Guardar cambios"}
            >
              <FaSave />
              {saving ? " Guardando…" : " Guardar"}
            </button>
            <button type="button" className="btn-fil" onClick={closeFn}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */
function Fila({ label, value }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        <div className="value">{value ?? ""}</div>
      </div>
    </div>
  );
}

function FilaEdit({ label, input, help }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        {input}
        {help && <div className="text-xs text-gray-500 mt-1">{help}</div>}
      </div>
    </div>
  );
}
